import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useIntelligenceStore } from '@/lib/stores/intelligence-store';
import { useUiStore } from '@/lib/stores/ui-store';
import { useVisitStore } from '@/lib/stores/visit-store';
import { visits as visitsApi } from '@/lib/api/api-client';
import { VisitStateCard } from '@/components/visits/VisitStateCard';
import { SoapPanel } from '@/components/intelligence/SoapPanel';
import { TranscriptPanel } from '@/components/intelligence/TranscriptPanel';
import { AlertsPanel } from '@/components/intelligence/AlertsPanel';
import { RunConsole } from '@/components/intelligence/RunConsole';
import { StripModeToggle } from '@/components/ui/StripModeToggle';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft } from 'lucide-react';
import type { VisitId } from '@assistmd/types';

/**
 * Single visit detail page — editorial layout with panel transitions.
 * Supports two layout modes:
 * - dock: Full panels (transcript, SOAP, alerts, run console)
 * - strip: Compact (SOAP note only)
 */
export function VisitDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const visitId = id as VisitId;

  const {
    transcriptEntries,
    interimEntry,
    soap,
    alerts,
    dismissAlert,
    processingStatus,
  } = useIntelligenceStore();

  // Read real visit state from the store (populated by WS Feed D + API)
  const visit = useVisitStore((s) => s.visits.find((v) => v.visitId === visitId));
  const addVisit = useVisitStore((s) => s.addVisit);
  const clearIntelligence = useIntelligenceStore((s) => s.clear);

  // Fetch visit from API if not in store (direct URL navigation / page refresh)
  useEffect(() => {
    if (!visit && visitId) {
      visitsApi.get(visitId).then((data) => {
        if (data && typeof data === 'object' && 'visitId' in data) {
          addVisit(data as any);
        }
      }).catch(() => { /* API unavailable — stay with empty state */ });
    }
  }, [visit, visitId, addVisit]);

  // Clear stale intelligence data when switching visits
  useEffect(() => {
    clearIntelligence();
    return () => { clearIntelligence(); };
  }, [visitId, clearIntelligence]);

  const panelMode = useUiStore((s) => s.panelMode);
  const showRunConsole = useUiStore((s) => s.showRunConsole);
  const showAlerts = useUiStore((s) => s.showAlerts);

  const isLoading = processingStatus === 'processing';

  return (
    <div className="animate-fade-in">
      {/* ── Header ── */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Back button */}
          <Link to="/visits" className="group">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              <span className="ml-1.5">Visits</span>
            </Button>
          </Link>

          {/* Vertical separator */}
          <Separator orientation="vertical" className="h-10" />

          {/* Icon container + title block */}
          <div className="flex items-center gap-3.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
            </div>
            <div>
              <p className="overline mb-0.5">Clinical Visit</p>
              <div className="flex items-center gap-3">
                <h1 className="page-title-serif">
                  Visit Detail
                </h1>
                <StatusBadge status={visit?.status ?? 'draft'} kind="visit" />
              </div>
              <p className="mt-0.5 font-mono text-xs text-slate-400">{visitId}</p>
            </div>
          </div>
        </div>

        <StripModeToggle />
      </div>

      {/* ── Divider ── */}
      <div className="divider-gradient mb-6" />

      {/* ── Visit metadata metric cards ── */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 animate-stagger-1">
        <div className="metric-card">
          <p className="metric-label">Patient</p>
          <p className="metric-value text-base">{visit?.patientCode ?? '---'}</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Visit ID</p>
          <p className="metric-value text-base">{visit?.visitId?.slice(0, 8) ?? '---'}</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Status</p>
          <div className="mt-1 flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${
              visit?.status === 'recording' ? 'bg-emerald-400 animate-pulse' :
              visit?.status === 'closed' ? 'bg-slate-300' :
              visit?.status === 'finalizing' ? 'bg-amber-400 animate-pulse' :
              'bg-teal-400'
            }`} />
            <p className="metric-value text-base capitalize">{visit?.status ?? 'draft'}</p>
          </div>
        </div>
        <div className="metric-card">
          <p className="metric-label">Created</p>
          <p className="metric-value text-base">
            {visit?.createdAt
              ? new Date(visit.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
              : '---'}
          </p>
        </div>
      </div>

      {/* Strip mode: compact SOAP-only view */}
      {panelMode === 'strip' ? (
        <div className="mx-auto max-w-2xl animate-scale-in">
          <div className="card overflow-hidden">
            <div className="px-5 pt-4 pb-0">
              <p className="section-header mb-4">SOAP Note</p>
            </div>
            <div className="px-5 pb-5">
              <SoapPanel soap={soap} loading={isLoading} />
            </div>
          </div>
        </div>
      ) : (
        /* Dock mode: full panel layout */
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left column: Transcript + SOAP */}
          <div className="space-y-6 lg:col-span-2">
            {/* Transcript panel */}
            <div className="animate-stagger-2">
              <div className="card overflow-hidden">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                  <p className="section-header">Live Transcript</p>
                  {isLoading && (
                    <span className="flex items-center gap-1.5 text-2xs font-medium text-teal-600">
                      <span className="recording-pulse" />
                      Processing
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <TranscriptPanel
                    entries={transcriptEntries}
                    interimEntry={interimEntry}
                    loading={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* SOAP panel */}
            <div className="animate-stagger-3">
              <div className="card overflow-hidden">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                  <p className="section-header">SOAP Note</p>
                  {soap && (
                    <span className="badge-teal text-2xs">
                      <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      Generated
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <SoapPanel soap={soap} loading={isLoading} />
                </div>
              </div>
            </div>
          </div>

          {/* Right column: State + Alerts + Run Console */}
          <div className="space-y-6">
            {/* Visit state card */}
            <div className="animate-stagger-2">
              <div className="card overflow-hidden">
                <div className="border-b border-slate-100 px-5 py-3">
                  <p className="section-header">Session State</p>
                </div>
                <div className="p-5">
                  <VisitStateCard
                    status={visit?.status ?? 'draft'}
                    consentState={visit?.consentState ?? 'unknown'}
                    isRecording={visit?.isRecording ?? false}
                    totalRecordingMs={visit?.totalRecordingMs ?? 0}
                    segmentCount={visit?.segmentCount ?? transcriptEntries.length}
                    wordCount={visit?.wordCount ?? transcriptEntries.reduce(
                      (acc, e) => acc + e.text.split(' ').length,
                      0,
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Alerts panel */}
            {showAlerts && (
              <div className="animate-stagger-3">
                <div className="card overflow-hidden">
                  <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                    <p className="section-header">Clinical Alerts</p>
                    {alerts.length > 0 && (
                      <span className="badge-amber text-2xs">
                        {alerts.length} active
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <AlertsPanel alerts={alerts} onDismiss={dismissAlert} />
                  </div>
                </div>
              </div>
            )}

            {/* Run console */}
            {showRunConsole && (
              <div className="animate-stagger-4">
                <div className="card overflow-hidden">
                  <div className="border-b border-slate-100 px-5 py-3">
                    <p className="section-header">Run Console</p>
                  </div>
                  <div className="p-5">
                    <RunConsole />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
